import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Form, Formik, useFormikContext } from "formik";
import Input from "../../../FormComponents/Input";
import Textarea from "../../../FormComponents/Textarea";
import FileDropzone from "../../../FormComponents/FileDropzone";
import CountrySelect from "../../../FormComponents/CountrySelect";
import InfoTooltip from "../../../FormComponents/InfoTooltip";
import { Image, Trash3 } from "react-bootstrap-icons";
import axiosService from "../../../../utils/AxiosService";
import { toastSuccess, toastError } from "../../../FormComponents/Toasts";
import { ManualAuthenticationSchema } from "../../../../schemas/ManualAuthenticationSchema";
import { providerLogo, formatProvider } from "../../../../utils/Helper";

const PROVIDERS = ['instagram', 'tiktok', 'youtube', 'linkedin', 'pinterest', 'podcast']

const EMPTY_VALUES = { username: '', reach: '', engagement_rate: '', country: '', description: '' }

const valuesFromAuthentication = authentication => ({
  username: authentication.username || '',
  reach: authentication.reach || '',
  engagement_rate: authentication.engagement_rate ? (authentication.engagement_rate * 100).toFixed(2) : '',
  country: authentication.country || '',
  description: authentication.description || '',
})

const sameValues = (a, b) => Object.keys(EMPTY_VALUES).every(key => String(a[key] ?? '') === String(b[key] ?? ''))

const isDraftDirty = draft => !!draft.avatarFile || !sameValues(draft.values, draft.baseline)

const errorsFromValidation = validationError => (validationError.inner || []).reduce(
  (result, error) => (error.path && !result[error.path] ? { ...result, [error.path]: error.message } : result),
  {}
)

function DraftSync({ onChange }) {
  const { values } = useFormikContext()
  useEffect(() => { onChange(values) }, [values])
  return null
}

export default observer(function ManualAuthenticationForm({ setIsOpen, onCancel, infId, authentications = [], provider, setProvider, drafts, setDrafts, onSuccess = () => {} }) {
  const [saving, setSaving] = useState(false)

  const manualAuthenticationFor = p => authentications.find(authentication => authentication.provider === p && authentication.manually_entered)
  const isModashManaged = p => authentications.some(authentication => authentication.provider === p && !authentication.manually_entered)

  const existing = provider ? manualAuthenticationFor(provider) : null
  const draft = provider ? drafts[provider] : null

  const fallbackCountry = () => (
    Object.values(drafts).find(d => d.values.country)?.values.country
    || authentications.find(authentication => authentication.country)?.country
    || ''
  )

  useEffect(() => {
    if (!provider || drafts[provider] || isModashManaged(provider)) return
    const existingAuthentication = manualAuthenticationFor(provider)
    const baseline = existingAuthentication ? valuesFromAuthentication(existingAuthentication) : { ...EMPTY_VALUES, country: fallbackCountry() }
    setDrafts(current => ({
      ...current,
      [provider]: { baseline, values: baseline, avatarFile: null, avatarPreview: null, errors: {}, validationRun: 0, dirty: false },
    }))
  }, [provider, drafts[provider]])

  const updateDraft = (p, patch) => setDrafts(current => {
    if (!current[p]) return current
    const next = { ...current[p], ...patch }
    return { ...current, [p]: { ...next, dirty: isDraftDirty(next) } }
  })

  const dirtyProviders = PROVIDERS.filter(p => drafts[p]?.dirty)
  const targetProviders = dirtyProviders.length ? dirtyProviders : (draft ? [provider] : [])

  const buildFormData = (p, values, avatarFile) => {
    const formData = new FormData()
    formData.append('authentication[manual]', true)
    formData.append('authentication[influencer_id]', infId)
    formData.append('authentication[provider]', p)
    formData.append('authentication[username]', values.username)
    formData.append('authentication[reach]', values.reach)
    formData.append('authentication[engagement_rate]', values.engagement_rate === '' ? '' : Number(values.engagement_rate) / 100)
    formData.append('authentication[country]', values.country || '')
    formData.append('authentication[description]', values.description || '')
    if (avatarFile) formData.append('authentication[avatar]', avatarFile)
    return formData
  }

  const saveAll = async () => {
    if (saving || !targetProviders.length) return

    const invalid = []
    for (const p of targetProviders) {
      try {
        await ManualAuthenticationSchema.validate(drafts[p].values, { abortEarly: false })
      } catch (validationError) {
        invalid.push({ provider: p, errors: errorsFromValidation(validationError) })
      }
    }
    if (invalid.length) {
      setDrafts(current => invalid.reduce((result, { provider: p, errors }) => ({
        ...result,
        [p]: { ...result[p], errors, validationRun: (result[p].validationRun || 0) + 1 },
      }), current))
      setProvider(invalid[0].provider)
      toastError(`Please check the ${invalid.map(({ provider: p }) => formatProvider(p)).join(', ')} details`)
      return
    }

    setSaving(true)
    const saved = []
    const failed = []
    for (const p of targetProviders) {
      const { values, avatarFile } = drafts[p]
      const wasExisting = !!manualAuthenticationFor(p)
      try {
        await axiosService.post('/api/project_manager/authentications', buildFormData(p, values, avatarFile), { headers: { "Content-type": "multipart/form-data" } })
        saved.push({ provider: p, wasExisting })
      } catch (response) {
        failed.push({ provider: p, message: response?.data?.error || 'Something went wrong' })
      }
    }
    setSaving(false)

    if (saved.length) {
      setDrafts(current => saved.reduce((result, { provider: p }) => {
        const { [p]: _removed, ...rest } = result
        return rest
      }, current))
      onSuccess()
    }

    if (failed.length) {
      failed.forEach(({ provider: p, message }) => toastError(`${formatProvider(p)}: ${message}`))
      if (saved.length) toastSuccess(`Saved: ${saved.map(({ provider: p }) => formatProvider(p)).join(', ')}`)
      setProvider(failed[0].provider)
      return
    }

    if (saved.length === 1) toastSuccess(saved[0].wasExisting ? 'Account updated' : 'Account created')
    else toastSuccess(`${saved.length} accounts saved`)
    setIsOpen(false)
  }

  const saveLabel = () => {
    if (saving) return 'Saving...'
    if (dirtyProviders.length > 1) return `Save ${dirtyProviders.length} accounts`
    return existing ? 'Save' : 'Create'
  }

  const avatarSrc = draft?.avatarPreview || existing?.avatar?.url

  return (
    <div className="flex flex-col">
      <div className="form-item-container w-full pt-4">
        <p className="text-sm pl-4 pb-1 dark:text-white inline-flex items-center gap-1">
          Provider
          <InfoTooltip text="Not synced with Modash - all fields below are entered and kept up to date by hand." />
        </p>
        <span role="group" className="ml-4 inline-flex items-center gap-4 flex-wrap rounded-lg bg-rb-grey dark:bg-rb-dark-mode-lighter p-4">
          {PROVIDERS.map(p => {
            const disabled = isModashManaged(p)
            return (
              <button
                type="button"
                key={p}
                title={disabled ? `${formatProvider(p)} is Modash-managed for this influencer` : formatProvider(p)}
                disabled={disabled}
                onClick={() => setProvider(p)}
                className={`relative ${provider === p ? 'bg-rb-soft-green dark:bg-rb-green/20 border-rb-green dark:border-rb-green' : 'bg-white dark:bg-rb-dark-mode'} ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'} w-12 h-12 flex items-center justify-center border-1 dark:border-rb-dark-mode-lighter rounded-md`}
              >
                {providerLogo(p)}
                {drafts[p]?.dirty && <span title="Unsaved changes" className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-rb-green border-2 border-white dark:border-rb-dark-mode" />}
              </button>
            )
          })}
        </span>
        {existing && (
          <p className="text-sm text-rb-green ml-4 mt-2">Editing the existing manual {formatProvider(provider)} account.</p>
        )}
        {dirtyProviders.length > 1 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 ml-4 mt-1">Unsaved changes on {dirtyProviders.map(formatProvider).join(', ')} - Save stores all of them.</p>
        )}
      </div>

      {draft && !isModashManaged(provider) && (
        <Formik
          key={`${provider}:${draft.validationRun}`}
          initialValues={draft.values}
          initialErrors={draft.errors}
          initialTouched={Object.keys(draft.errors).reduce((result, key) => ({ ...result, [key]: true }), {})}
          validationSchema={ManualAuthenticationSchema}
          validateOnMount
          onSubmit={saveAll}
        >
          {({ handleBlur, handleChange, values, touched, errors, setFieldValue }) => (
            <Form className="w-[725px]">
              <DraftSync onChange={nextValues => updateDraft(provider, { values: nextValues })} />
              <div className="form-item-container w-full px-4 mt-4">
                <div className="flex justify-between gap-2">
                  <div className="flex flex-col justify-between">
                    <Input error={touched.username && errors.username} placeholder="Username" name='username' topMargin='mt-0' onChange={handleChange} onBlur={handleBlur} value={values.username} />
                    <div className="mt-5">
                      <Input error={touched.reach && errors.reach} placeholder="Followers" name='reach' type="number" topMargin='mt-0' onChange={handleChange} onBlur={handleBlur} value={values.reach} />
                    </div>
                    <div className="mt-5">
                      <Input addon="%" addonPosition="end" placeholder="Engagement rate" name='engagement_rate' type="number" topMargin='mt-0' onChange={handleChange} onBlur={handleBlur} value={values.engagement_rate} />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <Textarea topMargin='mt-0' rows={7} placeholder="Bio & description" name='description' onChange={handleChange} onBlur={handleBlur} value={values.description} />
                  </div>
                  <div className="flex flex-col items-center">
                    <label className="text-sm pb-2 dark:text-white">Profile Picture</label>
                    {avatarSrc ? (
                      <div className='relative'>
                        <img src={avatarSrc} alt="Profile pic" className="w-18 h-18 rounded-md object-cover" />
                        <button type="button" className="absolute inset-0 flex items-center justify-center hover:bg-black hover:bg-opacity-50 opacity-0 hover:opacity-100 rounded-md transition-all duration-200"
                          onClick={() => updateDraft(provider, { avatarFile: null, avatarPreview: null })}
                        >
                          <Trash3 className="text-white" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-18 h-18 rounded-md">
                        <FileDropzone
                          isCompact
                          uploadFiles={files => updateDraft(provider, { avatarFile: files[0], avatarPreview: URL.createObjectURL(files[0]) })}
                          icon={<Image />}
                          key='avatar'
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-5">
                  <CountrySelect
                    name='country'
                    onChange={country => setFieldValue('country', country?.value || '', true)}
                    handleBlur={handleBlur}
                    value={values.country}
                    isClearable
                  />
                </div>
              </div>
              <div className="flex justify-between pt-4 bg-white dark:bg-rb-dark-mode">
                <button type='button' className='rb-btn-cancel font-bold text-md ml p-2 px-4 rounded-lg disabled:opacity-50' disabled={saving} onClick={onCancel}>
                  Cancel
                </button>
                <button type="submit" className="ml-2 bg-rb-green text-white rounded-lg p-2 px-4 disabled:opacity-50" disabled={saving}>
                  {saveLabel()}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  )
})
